import { useCallback, useEffect, useMemo, useState } from 'react';
import { markCallLogsRead, subscribeToTeamCallLogs } from '../services/firestoreService';
import userTeamService from '../services/userTeamService';
import { countUnreadMissedCalls, isMissedInboundCall, isUnreadMissedCall } from '../utils/voiceCallUtils';

/** Missed inbound calls + voicemails on the user's team number. */
export function useTeamMissedCalls(user, { enabled = true } = {}) {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const smsConfig = useMemo(
    () =>
      user?.email && enabled
        ? userTeamService.getUserSmsConfig(user.email)
        : { smsNumber: null, smsEnabled: false },
    [user?.email, enabled]
  );

  useEffect(() => {
    if (!user?.email || !smsConfig.smsNumber) {
      setCallLogs([]);
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    const unsubscribe = subscribeToTeamCallLogs(smsConfig.smsNumber, (logs) => {
      setCallLogs(logs);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [user?.email, smsConfig.smsNumber]);

  const missedCalls = useMemo(
    () =>
      callLogs
        .filter(isMissedInboundCall)
        .map((log) => ({
          ...log,
          unread: isUnreadMissedCall(log, user?.email),
        })),
    [callLogs, user?.email]
  );

  const unreadCount = useMemo(
    () => countUnreadMissedCalls(callLogs, user?.email),
    [callLogs, user?.email]
  );

  const markRead = useCallback(
    async (log) => {
      if (!log?.id || !user?.email) return;
      try {
        await markCallLogsRead([{ clientId: log.clientId || null, callLogId: log.id }], user.email);
      } catch (err) {
        console.error('Failed to mark call log read:', err);
      }
    },
    [user?.email]
  );

  const markAllRead = useCallback(async () => {
    if (!user?.email) return;
    const unread = missedCalls.filter((log) => log.unread);
    if (unread.length === 0) return;
    try {
      await markCallLogsRead(
        unread.map((log) => ({ clientId: log.clientId || null, callLogId: log.id })),
        user.email
      );
    } catch (err) {
      console.error('Failed to mark call logs read:', err);
    }
  }, [missedCalls, user?.email]);

  return {
    /** Every call on the team number (answered, outgoing, missed, voicemail), newest first. */
    callLogs,
    missedCalls,
    unreadCount,
    loading,
    hasNumber: Boolean(smsConfig.smsNumber),
    markRead,
    markAllRead,
  };
}

/** Header-badge count of unread missed calls/voicemails. */
export function useTeamMissedCallUnreadCount(user, { enabled = true } = {}) {
  const { unreadCount } = useTeamMissedCalls(user, { enabled });
  return unreadCount;
}
